import { canAccessRoute, isAdmin, PERFIL_LABEL, systemName, systemSubtitle } from '@supply/contracts'
import { getInitials } from '@/lib/format'
import { Eye, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Search } from 'lucide-react'
import * as React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { BrandMark } from '@/components/brand'
import { LiveIndicator } from '@/components/live-indicator'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { NotificationBell } from '@/modules/notifications/NotificationBell'
import { useSession, useSettings } from '@/lib/session'
import { cn } from '@/lib/utils'
import { isMockActive, VIEW_AS_MOCK_BLOCKED } from '@/data/mock/mode'
import { isViewingAs } from '@/lib/view-as'

import { ALL_NAV_ITEMS, NAV, routeTrail, visibleSettingsSections, type NavItem } from './nav'
import { badgeCount, NAV_BADGE_TONE, navBadge, navRouteOf, type NavBadge, type NavCounters } from './nav-counters'
import { useNavCounters } from './use-nav-counters'
import { MockModeBanner, MockModeMenuButton } from './MockMode'
import { PresenceStack } from './PresenceStack'
import { SupportButton } from './SupportButton'
import { ViewAsBanner, ViewAsDialog } from './ViewAs'

/**
 * Casca do app no molde do Design System Flexibase (PMC): sidebar 256/64px com
 * barra indigo na rota ativa, topbar de 56px com breadcrumb, ⌘K e tema.
 * No celular a sidebar vira gaveta.
 */

const COLLAPSE_KEY = 'supply-sidebar-collapsed'

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1'
  } catch {
    return false
  }
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = React.useState(readCollapsed)
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [searchOpen, setSearchOpen] = React.useState(false)
  const [viewAsOpen, setViewAsOpen] = React.useState(false)
  const location = useLocation()
  const settings = useSettings()
  const { isGestor } = useSession()
  const counters = useNavCounters(navRouteOf(location.pathname, NAV_ROUTES))

  React.useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0')
    } catch {
      /* storage indisponível */
    }
  }, [collapsed])

  React.useEffect(() => setMobileOpen(false), [location.pathname])

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen((o) => !o)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="flex h-dvh w-full flex-col overflow-hidden bg-background text-foreground">
      <ViewAsBanner />
      <MockModeBanner />
      <div className="flex min-h-0 w-full flex-1 overflow-hidden">
      <div className="hidden md:flex">
        <Sidebar collapsed={collapsed} counters={counters} onToggle={() => setCollapsed((c) => !c)} />
      </div>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[272px] p-0">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <Sidebar collapsed={false} counters={counters} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-background/90 px-3 backdrop-blur sm:px-4">
          <Button variant="ghost" size="icon-sm" className="md:hidden" aria-label="Abrir menu" onClick={() => setMobileOpen(true)}>
            <Menu />
          </Button>
          <nav aria-label="breadcrumb" className="min-w-0 truncate text-[13px] text-muted-foreground">
            <span className="hidden sm:inline">{systemName(settings)}</span>
            <span className="hidden px-1.5 opacity-50 sm:inline">/</span>
            {routeTrail(location.pathname).map((label, i, trail) => (
              <React.Fragment key={label}>
                {i > 0 && <span className="px-1.5 opacity-50">/</span>}
                <span className={i === trail.length - 1 ? 'text-foreground' : undefined}>{label}</span>
              </React.Fragment>
            ))}
          </nav>
          <span className="ml-auto" />
          {isGestor && !isViewingAs() && (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* Com dados fictícios o "ver como" fica desligado (SIS-465): o span segura o tooltip do botão desabilitado. */}
                <span tabIndex={isMockActive() ? 0 : -1}>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Ver como outra pessoa"
                    disabled={isMockActive()}
                    onClick={() => setViewAsOpen(true)}
                  >
                    <Eye />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{isMockActive() ? VIEW_AS_MOCK_BLOCKED : 'Ver como… (somente leitura)'}</TooltipContent>
            </Tooltip>
          )}
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="flex h-8 items-center gap-2 rounded-md border border-input bg-background px-2.5 text-[12px] text-muted-foreground transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-64"
            aria-label="Buscar (Ctrl+K)"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="hidden flex-1 text-left sm:inline">Buscar tela ou tarefa</span>
            <kbd className="hidden rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] sm:inline">Ctrl K</kbd>
          </button>
          <div className="flex items-center gap-1.5">
            <LiveIndicator />
            <PresenceStack />
          </div>
          <SupportButton />
          <NotificationBell />
          <ThemeToggle className="hidden sm:flex" />
        </header>
        {/* Só o `main` rola; `html`/`body` nunca. O `relative` faz do main o bloco de
            contenção dos `absolute` sem pai posicionado (ex.: `sr-only`): sem ele, esses
            spans ficam fora do recorte, esticam o documento e aparece uma segunda
            barra de rolagem com área vazia depois do conteúdo. */}
        <main className="relative min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[1440px] px-4 py-5 pb-24 sm:px-6 lg:px-8 lg:py-8">{children}</div>
        </main>
      </div>
      </div>
      <CommandMenu open={searchOpen} onOpenChange={setSearchOpen} />
      {isGestor && !isMockActive() && <ViewAsDialog open={viewAsOpen} onOpenChange={setViewAsOpen} />}
    </div>
  )
}

/** Itens que a pessoa pode abrir: gestor vê tudo; gerente também Usuários (o time); executor, só o operacional. */
function useVisibleNav() {
  const { isGestor, isGerente, me } = useSession()
  return NAV.map((g) => ({
    ...g,
    items: g.items.filter((i) => (!i.gestorOnly || isGestor || (i.gerenteToo && isGerente)) && canAccessRoute(me, i.to)),
  })).filter((g) => g.items.length)
}

const NAV_ROUTES = ALL_NAV_ITEMS.map((i) => i.to)

function Sidebar({ collapsed, counters, onToggle }: { collapsed: boolean; counters: NavCounters; onToggle?: () => void }) {
  const { member, isGestor, isExecutor, me, areaById, signOut, settings } = useSession()
  const groups = useVisibleNav()
  const role = isAdmin(me)
    ? PERFIL_LABEL.admin
    : isGestor
    ? 'Gestor'
    : isExecutor
      ? `Executor · ${me.areas.map((a) => areaById.get(a.areaId)?.code).filter(Boolean).join(' · ')}`
      : me.areas.length
      ? me.areas.map((a) => areaById.get(a.areaId)?.code).filter(Boolean).join(' · ')
      : 'Leitura'

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <div className={cn('flex h-14 items-center gap-2.5 border-b border-sidebar-border px-3', collapsed && 'justify-center px-0')}>
        <BrandMark />
        {!collapsed && (
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-[13px] font-semibold leading-tight tracking-tight text-foreground">{settings['sistema.nome']}</span>
            <span className="truncate font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{systemSubtitle(settings)}</span>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label="Principal">
        {groups.map((g) => (
          <div key={g.label} className="mb-4">
            {!collapsed && (
              <div className="px-2 pb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{g.label}</div>
            )}
            <ul className="flex flex-col gap-0.5">
              {g.items.map((item) => (
                <SidebarLink key={item.to} item={item} collapsed={collapsed} counters={counters} />
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className={cn('flex items-center gap-2 border-t border-sidebar-border p-3', collapsed && 'flex-col')}>
        <Avatar className="h-8 w-8">
          <AvatarFallback className="text-[11px]">{getInitials(member.full_name)}</AvatarFallback>
        </Avatar>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-medium">{member.full_name}</div>
            <div className="truncate text-[11px] text-muted-foreground">{role}</div>
          </div>
        )}
        <MockModeMenuButton />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label="Sair" onClick={() => void signOut()}>
              <LogOut />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Sair</TooltipContent>
        </Tooltip>
        {onToggle && (
          <Button variant="ghost" size="icon-sm" aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'} onClick={onToggle}>
            {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
          </Button>
        )}
      </div>
    </aside>
  )
}

function SidebarLink({ item, collapsed, counters }: { item: NavItem; collapsed: boolean; counters: NavCounters }) {
  const location = useLocation()
  const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
  const Icon = item.icon
  const badge = navBadge(counters.get(item.to), active)
  const link = (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      aria-label={badge ? `${item.label}: ${badge.label}` : undefined}
      className={cn(
        'relative flex h-9 items-center gap-2.5 rounded-md px-2.5 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
        active
          ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground'
          : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
        collapsed && 'justify-center px-0',
      )}
    >
      {active && <span aria-hidden className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-primary" />}
      <span className="relative shrink-0">
        <Icon className="h-4 w-4" />
        {collapsed && badge && <CollapsedDot badge={badge} />}
      </span>
      {!collapsed && <span className="truncate">{item.label}</span>}
      {!collapsed && badge && <NavBadgeMarks badge={badge} />}
    </Link>
  )
  return (
    <li>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right">
            {item.label}
            {badge && <span className="block text-[11px] text-muted-foreground">{badge.label}</span>}
          </TooltipContent>
        </Tooltip>
      ) : (
        link
      )}
    </li>
  )
}

/**
 * Badge do item aberto (molde do contador de Chamados do TI.FLEXIBASE):
 * pendência = número na cor da pior severidade; novidade = número info, ou
 * ponto info ao lado da pendência. O texto vai no `aria-label` do link.
 */
function NavBadgeMarks({ badge }: { badge: NavBadge }) {
  return (
    <span aria-hidden className="ml-auto flex shrink-0 items-center gap-1">
      {badge.news > 0 && badge.pending > 0 && <span className={cn('h-1.5 w-1.5 rounded-full', NAV_BADGE_TONE.novidade.dot)} />}
      {badge.pending > 0 && badge.severity ? (
        <span className={cn('rounded px-1.5 font-mono text-[10px] font-semibold tabular-nums', NAV_BADGE_TONE[badge.severity].pill)}>
          {badgeCount(badge.pending)}
        </span>
      ) : (
        <span className={cn('rounded px-1.5 font-mono text-[10px] font-semibold tabular-nums', NAV_BADGE_TONE.novidade.pill)}>
          {badgeCount(badge.news)}
        </span>
      )}
    </span>
  )
}

/** Sidebar recolhida: só um ponto no canto do ícone, na cor do que pesa mais. */
function CollapsedDot({ badge }: { badge: NavBadge }) {
  const tone = badge.severity ? NAV_BADGE_TONE[badge.severity] : NAV_BADGE_TONE.novidade
  return <span aria-hidden className={cn('absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full ring-2 ring-sidebar', tone.dot)} />
}

/** ⌘K: telas e "abrir tarefa SUP-123". */
function CommandMenu({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate()
  const groups = useVisibleNav()
  const { isGestor, isAdmin, me } = useSession()
  const sections = canAccessRoute(me, '/configuracoes') ? visibleSettingsSections(isGestor, isAdmin) : []
  const [search, setSearch] = React.useState('')
  const key = /^\s*(sup-?)?(\d+)\s*$/i.exec(search)

  const go = (to: string) => {
    onOpenChange(false)
    setSearch('')
    void navigate(to)
  }

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Tela ou número da tarefa (ex.: SUP-12)" value={search} onValueChange={setSearch} />
      <CommandList>
        <CommandEmpty>Nada encontrado.</CommandEmpty>
        {key && (
          <CommandGroup heading="Tarefa">
            <CommandItem value={`abrir SUP-${key[2]}`} onSelect={() => go(`/quadro?tarefa=SUP-${key[2]}`)}>
              Abrir SUP-{key[2]}
            </CommandItem>
          </CommandGroup>
        )}
        {groups.map((g) => (
          <CommandGroup key={g.label} heading={g.label}>
            {g.items.map((i) => (
              <CommandItem key={i.to} value={`${i.label} ${i.keywords ?? ''}`} onSelect={() => go(i.to)}>
                <i.icon className="mr-2 h-4 w-4" /> {i.label}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
        {sections.length > 0 && (
          <CommandGroup heading="Configurações">
            {sections.map((s) => (
              <CommandItem key={s.slug} value={`configurações ${s.label} ${s.keywords ?? ''}`} onSelect={() => go(`/configuracoes/${s.slug}`)}>
                <s.icon className="mr-2 h-4 w-4" /> {s.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  )
}
